import React from 'react';
import { Checkbox, Divider, Input, Button as NUIButton, Select, Selection, SelectItem } from '@nextui-org/react';
import { useNavigate } from 'react-router';
import FamiliesTable from '../../components/tables/FamiliesTable';
import { useSelector } from 'react-redux';
import { StoreState } from '../../../store/reducer-main';
import { TablePaginationConfig } from '../../../types/common';
import { Family, FamilyAssignedCaregiverData, FamilyWithAssignCaregiverData } from '../../../types/families';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { deleteUserFamily, getFamiliesTableMaxPageNumber, initFamiliesTable, loadFamiliesNextTablePage, loadFamiliesPreviousTablePage, removeCaregiverFromFamily, updateFamilyWarning, updateUserFamily } from '../../../service/families';
import { DEFAULT_TABLE_PAGINATION } from '../../../constants/pagination';
import { FamilyTableFilter } from '../../../types/filters';
import theme from '../../theme';
import PageLayout from '../../components/common/PageLayout';
import Typography from '../../components/common/Typography';
import Button from '../../components/common/Button';
import { getUserCaregiver } from '../../../service/caregiovers';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { toast } from 'react-toastify';
import TextButton from '../../components/common/TextButton';
import { CareGiverCovergaeAvailability } from '../../../types/caregivers';
import { CAREGIVER_COVERAGE_AVAILABILITY } from '../../../constants/caregivers';
import { brkRef } from '../../../helpers/utils';

export default function FamiliesPage() {

  const navigate = useNavigate();
  
  const { userProfile } = useSelector((state: StoreState) => state.profile);

  const [loading, setLoading] = React.useState<boolean>(false);
  // PAGINAZIONE
  const [familiesPage, setFamiliesPage] = React.useState<FamilyWithAssignCaregiverData[]>([]);
  const [paginationConfig, setPaginationConfig] = React.useState<TablePaginationConfig>(DEFAULT_TABLE_PAGINATION);
  const [totalNumberOfPages, setTotalNumberOfPages] = React.useState<number>(1);
  // FILTERS
  const [surnameFilter, setSurnameFilter] = React.useState<string>('');
  const [nameFilter, setNameFilter] = React.useState<string>('');
  const [ageFilter, setAgeFilter] = React.useState<string>('');
  const [caregiverNotEngaed, setCaregiverNotEngaged] = React.useState<boolean>(false);
  const [caregiverEngaged, setCaregiverEngaged] = React.useState<boolean>(false);
  const [coverageFilter, setCoverageFilter] = React.useState<Selection>(new Set([]));

  React.useEffect(() => {
    init();
    return () => {}
  }, []);

  const init = async () => {
    if (!userProfile) return;
    const page = await initFamiliesTable(userProfile.uid);
    const newFamilyPage = await integrateFamilyPageWithAssignedCaregiverData(page.data, userProfile.uid);
    const nop = await getFamiliesTableMaxPageNumber(userProfile.uid, DEFAULT_TABLE_PAGINATION.size);
    setPaginationConfig({page: 1, maxPage: nop, size: DEFAULT_TABLE_PAGINATION.size, firstDoc: page.cursors.first, lastDoc: page.cursors.last, secondDoc: page.cursors.second})
    setFamiliesPage(newFamilyPage);
    setTotalNumberOfPages(nop);
  }

  const applyTableFilters = async (
    filters?: FamilyTableFilter
  ) => {
    if (!userProfile) return;
    let tableFilters = filters;
    if (!tableFilters) {
      tableFilters = constructTableFilterObj();
    }
    console.log({tableFilters});
    const page = await loadFamiliesNextTablePage(userProfile.uid, DEFAULT_TABLE_PAGINATION, tableFilters);
    const newFamilyPage = await integrateFamilyPageWithAssignedCaregiverData(page.data, userProfile.uid);
    const maxPageNumeber = await getFamiliesTableMaxPageNumber(userProfile.uid, DEFAULT_TABLE_PAGINATION.size, tableFilters);
    setPaginationConfig({page: 1, maxPage: maxPageNumeber, size: DEFAULT_TABLE_PAGINATION.size, firstDoc: page.cursors.first, lastDoc: page.cursors.last, secondDoc: page.cursors.second})
    setTotalNumberOfPages(maxPageNumeber);
    setFamiliesPage(newFamilyPage);
  }

  const discardFilters = () => {
    setNameFilter('');
    setSurnameFilter('');
    setAgeFilter('');
    setCaregiverEngaged(false);
    setCaregiverNotEngaged(false);
    setCoverageFilter(new Set([]));
    init();
  }

  const integrateFamilyPageWithAssignedCaregiverData = async (
    page: Family[],
    uid: string,
  ): Promise<FamilyWithAssignCaregiverData[]> => {
    const newFamilyPage: FamilyWithAssignCaregiverData[] = [];
    for (const family of page) {
      const assignedCaregiver = family.assignedCaregiver;
      let familyAssignedCargiverData: FamilyAssignedCaregiverData | null = null;
      if (assignedCaregiver) {
        const caregiverData = await getUserCaregiver(uid, assignedCaregiver);
        familyAssignedCargiverData = { name: caregiverData.personalData.name, surname: caregiverData.personalData.surname };
      }
      newFamilyPage.push({...family, assignedCaregiverData: familyAssignedCargiverData });
    }
    return newFamilyPage;
  }

  const fetchNextPage = async() => {
    if (!userProfile) return;
    const { page, size, lastDoc, maxPage } = paginationConfig;
    if (!lastDoc) throw 'Invalid Last doc';
    const filters = constructTableFilterObj();
    const nextPagePaginationConfig: TablePaginationConfig = { page: page + 1, size, lastDoc, maxPage };
    const nextPage = await loadFamiliesNextTablePage(userProfile.uid, nextPagePaginationConfig, filters);
    const newFamilyPage = await integrateFamilyPageWithAssignedCaregiverData(nextPage.data, userProfile.uid);
    setPaginationConfig({page: nextPagePaginationConfig.page, size, firstDoc: nextPage.cursors.first, secondDoc: nextPage.cursors.second, lastDoc: nextPage.cursors.last, maxPage})
    setFamiliesPage(newFamilyPage);
  }

  const fetchPrevPage = async () => {
    if (!userProfile) return;
    const { page, size, firstDoc, maxPage } = paginationConfig;
    if (!firstDoc) throw 'Invalid Last doc';
    const filters = constructTableFilterObj();
    const prevPagePaginationConfig: TablePaginationConfig = { page: page - 1, size, firstDoc, maxPage };
    const prevPage = await loadFamiliesPreviousTablePage(userProfile.uid, prevPagePaginationConfig, filters);
    const newFamilyPage = await integrateFamilyPageWithAssignedCaregiverData(prevPage.data, userProfile.uid);
    setPaginationConfig({page: prevPagePaginationConfig.page, size, firstDoc: prevPage.cursors.first, secondDoc: prevPage.cursors.second, lastDoc: prevPage.cursors.last, maxPage})
    setFamiliesPage(newFamilyPage);
  }

  const constructTableFilterObj = (): FamilyTableFilter => {
    const coverage_f = Array.from(coverageFilter).length > 0 
      ? Array.from(coverageFilter)[0] as CareGiverCovergaeAvailability 
      : null;
    return {
      name: nameFilter || null,
      surname: surnameFilter || null,
      age: ageFilter || null,
      noCaregiver: caregiverNotEngaed,
      withCaregiver: caregiverEngaged,
      coverage: coverage_f,
    }
  }

  const onFamilyDelete = async (
    family: Family
  ) => {
    try {
      if (!userProfile) return;
      await deleteUserFamily(userProfile.uid, family);
      discardFilters();
      toast.success('Famiglia eliminata con successo');
    } catch (error) {
      console.log('err', error);
      toast.error("Errore durante l'eliminazione della famiglia")
    }
  }

  const onCaregiverRemove = async (
    family: Family
  ) => {
    try {
      if (!userProfile) return;
      const caregiverId = family.assignedCaregiver;
      if (!caregiverId) return;
      await removeCaregiverFromFamily(userProfile.uid, family.id, caregiverId);
      discardFilters();
      toast.success('Badante rimossa con successo');
    } catch (error) {
      console.log('err', error);
      toast.error('Si è verificato un errore durante la rimozione della badante');
    }
  }

  const onFamilyEdit = (
    family: Family
  ) => {
    navigate('/edit_family', { state: family });
  }

  const gotoCreateFamily = () => {
    navigate('/create_family');
  }

  const onNameChange = (
    val: string
  ) => {
    setNameFilter(val);
    const t_filters = constructTableFilterObj();
    t_filters.name = val;
    applyTableFilters(t_filters);
  }

  const onSurnameChange = (
    val: string
  ) => {
    setSurnameFilter(val);
    const t_filters = constructTableFilterObj();
    t_filters.surname = val;
    applyTableFilters(t_filters);
  }

  const onWarningToggle = async (
    id: string,
    value: boolean
  ) => {
    try {
      if (!userProfile) return;
      console.log(id);
      const idx = familiesPage.findIndex(el => el.id === id);
      if (idx >= 0) {
        const targetFamily = familiesPage[idx];
        await updateFamilyWarning(userProfile.uid, targetFamily.id, value);
        setFamiliesPage((prev) => {
          const newFamilies = brkRef(prev);
          newFamilies[idx].warning = value;
          return newFamilies;
        })
      }
    } catch (error) {
      toast.error('Si è verificato un errore');
    }
  }
  
  return (
    <PageLayout nestedPage>
      <div className='flex justify-between items-center'>
        <div className='flex flex-col'>
          <Typography variant='headline3'>
            Famiglie
          </Typography>
          <Typography className='mt-2'>
            Lista famiglie create
          </Typography>
        </div>
        <TextButton
          icon={<AddCircleIcon style={{color: theme.color.primary}}/>}
          onPress={gotoCreateFamily}
          title='NUOVA FAMIGLIA'/>
      </div>
      <div className='flex flex-col w-full h-full mt-6 p-4' style={{
        borderRadius: theme.sizing.x2,
        backgroundColor: theme.color.background,
        border: '1.5px solid lightgray'
      }}>
        <Typography variant='button' style={{fontWeight: 600}}>Filtri</Typography>
        <div className='flex flex-row gap-8 items-center mt-2 justify-between'>
          <Input
            label='Nome'
            className='flex-1' 
            value={nameFilter} 
            onValueChange={onNameChange}/>
          <Input
            label='Cognome'
            className='flex-1' 
            value={surnameFilter} 
            onValueChange={onSurnameChange}/>
          <Input
            label='Età'
            className='flex-1' 
            value={ageFilter} 
            onValueChange={(val) => {
              setAgeFilter(val);
            }}/>
          <Select
            label='Copertura'
            className='flex-1 min-w-[150px]'
            selectionMode='single'
            selectedKeys={coverageFilter}
            onSelectionChange={setCoverageFilter}>
              {
                CAREGIVER_COVERAGE_AVAILABILITY.map(c => (
                  <SelectItem key={c.key}>
                    {c.value}
                  </SelectItem>
                ))
              }
          </Select>
          <Checkbox isSelected={caregiverNotEngaed} onValueChange={setCaregiverNotEngaged}>
            Non assunta
          </Checkbox>
          <Checkbox isSelected={caregiverEngaged} onValueChange={setCaregiverEngaged}>
            Assunta
          </Checkbox>
          <div className='flex flex-row gap-4 itmes-center'>
            <Button title='Applica' variant='primary' onClick={() => applyTableFilters()} />
            <Button title='Cancella' variant='secondary' onClick={discardFilters} />
          </div>
        </div>
        <Divider className='mt-4'/>
        <FamiliesTable 
          className='mt-8' 
          data={familiesPage}
          onDelete={onFamilyDelete}
          onWarningToggle={onWarningToggle}
          onCaregiverRemove={onCaregiverRemove}
          onEdit={onFamilyEdit} />
        <div className='flex justify-end gap-4 items-center mt-auto'>
          <NUIButton isIconOnly size='sm' onClick={fetchPrevPage} isDisabled={paginationConfig.page === 1}>
            <ArrowBackIosNewIcon fontSize='small'/>
          </NUIButton>
          <p>{paginationConfig.page}</p>
          <NUIButton isIconOnly size='sm' onClick={fetchNextPage} isDisabled={paginationConfig.page >= totalNumberOfPages}>
            <ArrowForwardIosIcon fontSize='small'/>
          </NUIButton>
        </div>
      </div>
    </PageLayout>
  )
}